import { useState, useCallback, useRef } from "react";

import { DropzoneError } from "src/shared/components";
import { useGetFiles } from "src/shared/hooks";
import { useUploadFile } from "src/shared/hooks/useUploadFile";
import { HackerAPIFile } from "src/shared/utils/hackerapi";

export const useFileDropzone = (
  files: HackerAPIFile[] | undefined,
  setFiles: (files: HackerAPIFile[] | undefined) => void,
  maxFiles = 1
) => {
  const currentFiles = files ?? [];

  const {
    uploadFile,
    uploadFiles,
    mutationResult: { loading: isFileUploading, error: fileUploadError },
  } = useUploadFile();
  const [dropzoneErrorMessage, setDropzoneErrorMessage] = useState("");
  const dropzoneInputRef = useRef<HTMLInputElement>();
  const fetchedFiles = useGetFiles(currentFiles);
  const fetchedFile = fetchedFiles[0];

  const error = dropzoneErrorMessage || fileUploadError?.message;
  const loading = isFileUploading;

  const addFiles = async (newFiles: File[]) => {
    if (maxFiles === 1) {
      const hapiFile = await uploadFile(newFiles[0]);
      if (hapiFile) setFiles([hapiFile]);
      return;
    }

    const hapiFiles = await uploadFiles(newFiles);
    const successfulFiles = hapiFiles.filter(
      (file) => !!file
    ) as HackerAPIFile[];
    const allFiles = [...currentFiles, ...successfulFiles];

    if (allFiles.length > maxFiles) {
      setDropzoneErrorMessage(`Please upload at most ${maxFiles} files.`);
      return;
    }

    setFiles(allFiles.length > 0 ? allFiles : undefined);
  };

  const deleteFile = (index?: number) => {
    if (maxFiles === 1 || index === undefined) {
      setFiles(undefined);
    } else {
      setFiles(currentFiles.filter((_item, i) => i !== index));
    }
    if (dropzoneInputRef.current) dropzoneInputRef.current.value = "";
  };

  const onFileError = useCallback(
    (error: DropzoneError) => {
      switch (error) {
        case DropzoneError.MAX_AMOUNT:
          setDropzoneErrorMessage(
            maxFiles === 1
              ? "Please upload a single file."
              : `Please upload at most ${maxFiles} files.`
          );
          return;
        case DropzoneError.INVALID_FORMAT:
          setDropzoneErrorMessage("Please upload a valid file format.");
          return;
        default:
          setDropzoneErrorMessage("An error has occurred.");
      }
    },
    [maxFiles]
  );

  return {
    dropzoneInputRef,
    fetchedFile,
    fetchedFiles,
    loading,
    error,
    addFiles,
    deleteFile,
    onFileError,
    setDropzoneErrorMessage,
  };
};
